import { Component, OnInit } from '@angular/core';
import { forkJoin, take } from 'rxjs';
import { APIResponse } from 'fakturnia-shared';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { SubscriptionsApiService, SubscriptionPlan, FeatureComparison, Order } from 'shared/services/api/subscriptions.api.service';
import { SubscriptionPlanDialogComponent } from './subscription-plan-dialog/subscription-plan-dialog.component';
import { OrderDetailsDialogComponent } from './order-details-dialog/order-details-dialog.component';
import { SnackbarService } from 'shared/services/snackbar.service';
import { DialogService } from 'shared/services/dialog.service';
import { DateTime } from 'luxon';

type FeatureValue = 'true' | 'false' | 'custom';

interface EditableFeature {
  _id?: string;
  name: string;
  order: number;
  freeType: FeatureValue;
  freeCustom: string;
  startType: FeatureValue;
  startCustom: string;
  proType: FeatureValue;
  proCustom: string;
  businessType: FeatureValue;
  businessCustom: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatMenuModule,
    MatDividerModule,
    DragDropModule
  ],
  selector: 'app-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss']
})
export class SubscriptionsComponent implements OnInit {

  plans: SubscriptionPlan[] = [];
  isLoading = true;
  displayedColumns: string[] = ['type', 'name', 'price', 'duration', 'status', 'actions'];

  // Feature Comparisons
  features: EditableFeature[] = [];
  isFeaturesLoading = false;
  isFeaturesSaving = false;
  featuresHasChanges = false;

  // Orders
  orders: Order[] = [];
  filteredOrders: Order[] = [];
  isOrdersLoading = false;
  ordersStatusFilter: 'all' | 'pending' | 'paid' | 'cancelled' = 'all';
  ordersSearchTerm = '';
  ordersDisplayedColumns: string[] = ['orderNumber', 'user', 'plan', 'amount', 'paymentMethod', 'status', 'invoice', 'ksef', 'createdAt', 'actions'];

  // Kolejność stage do sortowania
  private typeOrder: Record<number, number> = {
    0: 0,
    1: 1,
    2: 2,
    3: 3
  };

  // Stats
  get activePlansCount(): number {
    return this.plans.filter(p => p.isActive).length;
  }

  get discountedPlansCount(): number {
    return this.plans.filter(p => p.discountPercent && p.discountPercent > 0).length;
  }

  get paidPlansCount(): number {
    return this.plans.filter(p => p.stage !== 0).length;
  }

  // Orders stats
  get pendingOrdersCount(): number {
    return this.orders.filter(o => o.status === 'pending').length;
  }

  get paidOrdersCount(): number {
    return this.orders.filter(o => o.status === 'paid').length;
  }

  get totalRevenue(): number {
    return this.orders
      .filter(o => o.status === 'paid')
      .reduce((sum, o) => sum + o.amount, 0);
  }

  // Zamówienia opłacone bez wygenerowanej faktury
  get paidWithoutInvoiceCount(): number {
    return this.orders.filter(o => o.status === 'paid' && !o.invoiceGenerated).length;
  }

  // Zamówienia z fakturą do wysłania do KSeF
  get invoiceToSendToKsefCount(): number {
    return this.orders.filter(o => o.invoiceGenerated && !o.ksefNumber).length;
  }

  // Łączna liczba akcji do wykonania
  get actionsRequiredCount(): number {
    return this.paidWithoutInvoiceCount + this.invoiceToSendToKsefCount;
  }

  constructor(
    private _subscriptionsApi: SubscriptionsApiService,
    private _dialog: MatDialog,
    private _snackbar: SnackbarService,
    private _dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this._loadData();
    this._loadOrders();
  }

  private _loadData() {
    this.isLoading = true;
    
    forkJoin({
      plans: this._subscriptionsApi.getPlans().pipe(take(1)),
      features: this._subscriptionsApi.getFeatureComparisons().pipe(take(1))
    }).subscribe({
      next: (responses: { plans: APIResponse; features: APIResponse }) => {
        // Plans
        const plansData = responses.plans.data || [];
        this.plans = plansData.sort((a: SubscriptionPlan, b: SubscriptionPlan) => {
          return (a.stage ?? 99) - (b.stage ?? 99);
        });

        // Features
        const featuresData: FeatureComparison[] = responses.features.data || [];
        this.features = featuresData.map((f, idx) => this._toEditableFeature(f, idx));
        this.featuresHasChanges = false;

        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private _loadPlans() {
    this._subscriptionsApi.getPlans().pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        const plansData = response.data || [];
        this.plans = plansData.sort((a: SubscriptionPlan, b: SubscriptionPlan) => {
          return (a.stage ?? 99) - (b.stage ?? 99);
        });
      }
    });
  }

  private _loadFeatures() {
    this.isFeaturesLoading = true;
    this._subscriptionsApi.getFeatureComparisons().pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        const featuresData: FeatureComparison[] = response.data || [];
        this.features = featuresData.map((f, idx) => this._toEditableFeature(f, idx));
        this.featuresHasChanges = false;
        this.isFeaturesLoading = false;
      },
      error: () => {
        this.isFeaturesLoading = false;
      }
    });
  }

  private _toEditableFeature(f: FeatureComparison, idx: number): EditableFeature {
    console.log('[Feature] Raw from API:', f.name, {
      free: f.free,
      freeType: typeof f.free,
      start: f.start,
      startType: typeof f.start,
      pro: f.pro,
      proType: typeof f.pro
    });
    
    const result = {
      _id: f._id,
      name: f.name,
      order: f.order ?? idx,
      freeType: this._getValueType(f.free),
      freeCustom: this._getCustomValue(f.free),
      startType: this._getValueType(f.start),
      startCustom: this._getCustomValue(f.start),
      proType: this._getValueType(f.pro),
      proCustom: this._getCustomValue(f.pro),
      businessType: this._getValueType(f.business),
      businessCustom: this._getCustomValue(f.business)
    };
    
    console.log('[Feature] Converted:', f.name, {
      freeType: result.freeType,
      startType: result.startType,
      proType: result.proType
    });
    
    return result;
  }

  private _getValueType(value: string | boolean | undefined): FeatureValue {
    console.log('[_getValueType] Input:', value, 'Type:', typeof value, 'Strict equals:', {
      'true': value === true,
      'false': value === false,
      '"true"': value === 'true',
      '"false"': value === 'false'
    });
    
    if (value === true || value === 'true') return 'true';
    if (value === false || value === 'false') return 'false';
    return 'custom';
  }

  private _getCustomValue(value: string | boolean | undefined): string {
    // Jeśli to "true" lub "false" (string) - nie traktuj jako custom
    if (value === 'true' || value === 'false' || typeof value === 'boolean') return '';
    if (typeof value === 'string') return value;
    return '';
  }

  private _fromEditableFeature(f: EditableFeature): FeatureComparison {
    return {
      _id: f._id,
      name: f.name,
      order: f.order,
      free: this._toApiValue(f.freeType, f.freeCustom),
      start: this._toApiValue(f.startType, f.startCustom),
      pro: this._toApiValue(f.proType, f.proCustom),
      business: this._toApiValue(f.businessType, f.businessCustom)
    };
  }

  private _toApiValue(type: FeatureValue, custom: string): string | boolean {
    if (type === 'true') return true;
    if (type === 'false') return false;
    return custom || '';
  }

  openCreateDialog() {
    const dialogRef = this._dialog.open(SubscriptionPlanDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      data: { plan: null, mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._loadPlans();
      }
    });
  }

  openEditDialog(plan: SubscriptionPlan) {
    const dialogRef = this._dialog.open(SubscriptionPlanDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      data: { plan, mode: 'edit' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._loadPlans();
      }
    });
  }

  formatPrice(priceInCents: number): string {
    return (priceInCents / 100).toFixed(2) + ' zł';
  }

  formatDuration(plan: SubscriptionPlan): string {
    if (plan.durationYears) {
      return `${plan.durationYears} ${plan.durationYears === 1 ? 'rok' : plan.durationYears < 5 ? 'lata' : 'lat'}`;
    }
    return `${plan.durationMonths} ${plan.durationMonths === 1 ? 'miesiąc' : plan.durationMonths < 5 ? 'miesiące' : 'miesięcy'}`;
  }

  getTypeLabel(stage: number): string {
    return String(stage);
  }

  getTypeColor(stage: number): string {
    const colors: Record<number, string> = {
      0: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      1: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      2: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      3: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  }

  hasDiscount(plan: SubscriptionPlan): boolean {
    return !!(plan.discountPercent && plan.discountPercent > 0 && plan.originalPrice);
  }

  // === Feature Comparisons Methods ===

  onFeatureChange() {
    this.featuresHasChanges = true;
  }

  addFeature() {
    const newFeature: EditableFeature = {
      name: '',
      order: this.features.length,
      freeType: 'false',
      freeCustom: '',
      startType: 'true',
      startCustom: '',
      proType: 'true',
      proCustom: '',
      businessType: 'true',
      businessCustom: ''
    };
    this.features.push(newFeature);
    this.featuresHasChanges = true;
  }

  removeFeature(index: number) {
    this.features.splice(index, 1);
    // Przelicz order
    this.features.forEach((f, idx) => f.order = idx);
    this.featuresHasChanges = true;
  }

  dropFeature(event: CdkDragDrop<EditableFeature[]>) {
    moveItemInArray(this.features, event.previousIndex, event.currentIndex);
    // Przelicz order
    this.features.forEach((f, idx) => f.order = idx);
    this.featuresHasChanges = true;
  }

  saveFeatures() {
    if (this.isFeaturesSaving) return;

    // Walidacja
    const emptyNames = this.features.filter(f => !f.name.trim());
    if (emptyNames.length > 0) {
      this._snackbar.showSnackBar('Wszystkie funkcjonalności muszą mieć nazwę', 'warning');
      return;
    }

    // Log przed zapisem
    console.log('[SaveFeatures] Current features state:', this.features.map(f => ({
      name: f.name,
      freeType: f.freeType,
      startType: f.startType,
      proType: f.proType
    })));

    this.isFeaturesSaving = true;
    const featuresData = this.features.map(f => this._fromEditableFeature(f));
    
    console.log('[SaveFeatures] Data to send:', featuresData.map(f => ({
      name: f.name,
      free: f.free,
      freeType: typeof f.free,
      start: f.start,
      pro: f.pro
    })));

    this._subscriptionsApi.saveFeatureComparisons(featuresData).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isFeaturesSaving = false;
        if (response.success) {
          this._snackbar.showSnackBar('Porównanie funkcjonalności zostało zapisane', 'success');
          // Odśwież dane
          const updatedFeatures: FeatureComparison[] = response.data || [];
          this.features = updatedFeatures.map((f, idx) => this._toEditableFeature(f, idx));
          this.featuresHasChanges = false;
        } else {
          this._snackbar.showSnackBar(response.message || 'Wystąpił błąd', 'warning');
        }
      },
      error: () => {
        this.isFeaturesSaving = false;
        this._snackbar.showSnackBar('Nie udało się zapisać danych', 'warning');
      }
    });
  }

  getValueTypeOptions(): { value: FeatureValue; label: string }[] {
    return [
      { value: 'true', label: '✓ Tak' },
      { value: 'false', label: '✗ Nie' },
      { value: 'custom', label: '# Własne' }
    ];
  }

  // === Orders Methods ===

  private _loadOrders() {
    this.isOrdersLoading = true;
    this._subscriptionsApi.getOrders().pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.orders = response.data || [];
        this.filterOrders();
        this.isOrdersLoading = false;
      },
      error: () => {
        this.isOrdersLoading = false;
      }
    });
  }

  refreshOrders() {
    this._loadOrders();
  }

  filterOrders() {
    let filtered = [...this.orders];

    // Filtruj po statusie
    if (this.ordersStatusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === this.ordersStatusFilter);
    }

    // Filtruj po search term (numer zamówienia, NIP, nazwa firmy)
    if (this.ordersSearchTerm.trim()) {
      const term = this.ordersSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(o => 
        o.orderNumber.toLowerCase().includes(term) ||
        o.invoiceData?.nip?.toLowerCase().includes(term) ||
        o.invoiceData?.companyName?.toLowerCase().includes(term)
      );
    }

    this.filteredOrders = filtered;
  }

  setOrdersStatusFilter(status: 'all' | 'pending' | 'paid' | 'cancelled') {
    this.ordersStatusFilter = status;
    this.filterOrders();
  }

  clearOrdersSearch() {
    this.ordersSearchTerm = '';
    this.filterOrders();
  }

  getOrderStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'pending': 'Oczekuje',
      'paid': 'Opłacone',
      'cancelled': 'Anulowane',
      'refunded': 'Zwrócone'
    };
    return labels[status] || status;
  }

  getOrderStatusClass(status: string): string {
    const classes: Record<string, string> = {
      'pending': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'paid': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'cancelled': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      'refunded': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    return classes[status] || 'bg-gray-100 text-gray-700';
  }

  getOrderStatusIcon(status: string): string {
    const icons: Record<string, string> = {
      'pending': 'tabler_outline:clock',
      'paid': 'tabler_outline:check',
      'cancelled': 'tabler_outline:x',
      'refunded': 'tabler_outline:arrow-back'
    };
    return icons[status] || 'tabler_outline:help';
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm');
  }

  viewOrderDetails(order: Order) {
    const dialogRef = this._dialog.open(OrderDetailsDialogComponent, {
      data: { order },
      panelClass: 'order-details-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'updated') {
        this._loadOrders();
      }
    });
  }

  // Sprawdza czy zamówienie oczekuje dłużej niż 7 dni
  isOldPendingOrder(order: Order): boolean {
    if (order.status !== 'pending') return false;
    const createdAt = DateTime.fromISO(order.createdAt);
    const now = DateTime.now();
    const diffDays = now.diff(createdAt, 'days').days;
    return diffDays > 7;
  }

  markOrderAsPaid(order: Order) {
    this._subscriptionsApi.updateOrderStatus(order._id!, 'paid').pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        if (response.success) {
          this._snackbar.showSnackBar('Zamówienie oznaczone jako opłacone', 'success');
          this._loadOrders();
        } else {
          this._snackbar.showSnackBar(response.message || 'Wystąpił błąd', 'warning');
        }
      },
      error: () => {
        this._snackbar.showSnackBar('Nie udało się zaktualizować zamówienia', 'warning');
      }
    });
  }

  cancelOrder(order: Order) {
    this._dialogService.confirmDialog({
      title: 'Anulowanie zamówienia',
      message: `Czy na pewno chcesz anulować zamówienie #${order.orderNumber}? Tej operacji nie można cofnąć.`,
      type: 'danger',
      confirmText: 'Anuluj zamówienie',
      cancelText: 'Nie anuluj'
    }).subscribe({
      next: (confirmed) => {
        if (confirmed !== true) return;

        this._subscriptionsApi.updateOrderStatus(order._id!, 'cancelled').pipe(take(1)).subscribe({
          next: (response: APIResponse) => {
            if (response.success) {
              this._snackbar.showSnackBar('Zamówienie zostało anulowane', 'success');
              this._loadOrders();
            } else {
              this._snackbar.showSnackBar(response.message || 'Wystąpił błąd', 'warning');
            }
          },
          error: () => {
            this._snackbar.showSnackBar('Nie udało się anulować zamówienia', 'warning');
          }
        });
      }
    });
  }

  generateInvoice(order: Order) {
    this._dialogService.confirmDialog({
      title: 'Generowanie faktury',
      message: `Czy na pewno chcesz wygenerować fakturę dla zamówienia #${order.orderNumber}? Dane do faktury zostaną zweryfikowane w bazie GUS.`,
      type: 'info',
      confirmText: 'Generuj fakturę',
      cancelText: 'Anuluj'
    }).subscribe({
      next: (confirmed) => {
        if (confirmed !== true) return;

        this._subscriptionsApi.generateInvoice(order._id!).pipe(take(1)).subscribe({
          next: (response: APIResponse) => {
            if (response.success) {
              if (response.data.gusWarning) {
                this._snackbar.showSnackBar(`Faktura wygenerowana. Uwaga: ${response.data.gusWarning}`, 'warning');
              } else {
                this._snackbar.showSnackBar(response.message || 'Faktura została wygenerowana', 'success');
              }
              this._loadOrders();
            } else {
              this._snackbar.showSnackBar(response.message || 'Nie udało się wygenerować faktury', 'warning');
            }
          },
          error: () => {
            this._snackbar.showSnackBar('Wystąpił błąd podczas generowania faktury', 'warning');
          }
        });
      }
    });
  }

  viewInvoice(order: Order) {
    // Otwórz szczegóły zamówienia gdzie jest faktura
    const dialogRef = this._dialog.open(OrderDetailsDialogComponent, {
      data: { order },
      panelClass: 'order-details-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'updated') {
        this._loadOrders();
      }
    });
  }

  getPaymentMethodLabel(method: string): string {
    const labels: Record<string, string> = {
      'transfer': 'Przelew tradycyjny',
      'payu': 'PayU',
      'przelewy24': 'Przelewy24'
    };
    return labels[method] || method;
  }
}
